/**
 * @file jobSearch.tsx
 *
 * @fileoverview Job search context to manage jobs data and current job selection
 * without triggering server-side refetch.
 */

import React, {
  Context,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { getJobById } from '~/jobiqo/components/Job/graphql/queries/jobQueries';
import { Job } from '~/jobiqo/types/types';
import { PathContext } from '~/jobiqo/context/app';
import { PathContextInterface } from '~/jobiqo/models/app';

export interface JobSearchContextInterface {
  /**
   * The current job ID selected.
   */
  currentJobId: string | null;
  /**
   * Sets the current job ID.
   */
  setCurrentJobId: (jobId: string | null) => void;
  /**
   * The current job details.
   */
  currentJob: Job | null;
  /**
   * Function to refetch the current job.
   */
  refetchJob: () => void;
}

/**
 * Initialize job search context.
 */
export const JobSearchContext: Context<JobSearchContextInterface> = createContext<
  JobSearchContextInterface
>({
  currentJobId: null,
  setCurrentJobId: () => {},
  currentJob: null,
  refetchJob: () => {},
});

interface JobSearchProviderProps {
  /**
   * Initial querystring containing jobId or backfillid if present.
   */
  initialQuerystring: any;
  /**
   * Initial job data if preloaded server-side.
   */
  initialJob?: Job | null;
  /**
   * Backend modules enabled for this request's GraphQL schema.
   */
  enabledModules?: string[];
  /**
   * The children elements.
   */
  children: React.ReactNode;
}

export default function JobSearchProvider({
  initialQuerystring,
  initialJob,
  enabledModules,
  children,
}: JobSearchProviderProps) {
  const pathContext: PathContextInterface = useContext(PathContext);
  // Explicit jobId initialization when page is loaded with ?jobId= in the URL.
  const initialJobIdValue = initialQuerystring?.jobId || initialJob?.id;
  const initialJobId = initialJobIdValue ? String(initialJobIdValue) : null;
  // Current job that's being shown.
  const [currentJobId, setCurrentJobId] = useState<string | null>(initialJobId);
  const [currentJob, setCurrentJob] = useState<Job | null>(initialJob || null);
  const previousInitialJobIdRef = useRef<string | null>(initialJobId);
  // Ref to keep track of previous jobId in URL to handle cleanup
  // when jobId is removed from URL (e.g. when filtering with a job selected).
  const previousUrlJobIdRef = useRef<string | null>(
    pathContext.querystring?.jobId
      ? String(pathContext.querystring.jobId)
      : null,
  );

  // This cleans up a previous job selected
  // (E.g. when filtering with a job selected)
  useEffect(() => {
    if (previousInitialJobIdRef.current !== initialJobId) {
      // if the initial job changed in the pages prop means that a new
      // search was triggered, so we need to clear the current job.
      previousInitialJobIdRef.current = initialJobId;
      setCurrentJobId(initialJobId);
    }
  }, [initialJobId]);

  useEffect(() => {
    setCurrentJob(initialJob || null);
  }, [initialJob]);

  // Sync currentJobId with URL when it changes.
  useEffect(() => {
    const urlJobId = pathContext.querystring?.jobId
      ? String(pathContext.querystring.jobId)
      : null;

    if (urlJobId && urlJobId !== currentJobId) {
      setCurrentJobId(urlJobId);
    }

    if (!urlJobId && previousUrlJobIdRef.current) {
      // Cleanup current job selection when the jobId is removed from the URL.
      // E.g. changing filter selection.
      setCurrentJobId(null);
    }

    previousUrlJobIdRef.current = urlJobId;
  }, [pathContext.querystring?.jobId, currentJobId]);

  const currentJobIdNumber = currentJobId ? Number(currentJobId) : null;
  const currentBackfillId =
    pathContext.querystring?.backfillid ||
    initialQuerystring?.backfillid ||
    null;

  // Determine if we should skip the job query.
  const shouldSkipJobQuery: boolean =
    !currentJobId ||
    !!(
      initialJob &&
      initialJob.id &&
      currentJobIdNumber === Number(initialJob.id) &&
      ((initialJob as any)?.backfillid || null) === (currentBackfillId || null)
    );

  const { refetch: refetchRegularJob } = useQuery(
    getJobById(null, enabledModules),
    {
      variables: {
        id: currentJobIdNumber,
        backfillid: currentBackfillId,
        getOrgProfileDetails: true,
      },
      fetchPolicy: 'no-cache',
      skip: shouldSkipJobQuery,
      onCompleted: (data) => {
        setCurrentJob(data?.jobById?.job || null);
      },
    },
  );

  const refetchJob = () => {
    if (currentJobId) {
      refetchRegularJob();
    }
  };

  // Clear current job when currentJobId is cleared.
  useEffect(() => {
    if (!currentJobId) {
      setCurrentJob(null);
    }
  }, [currentJobId]);

  return (
    <JobSearchContext.Provider
      value={{
        currentJobId,
        setCurrentJobId,
        currentJob,
        refetchJob,
      }}
    >
      {children}
    </JobSearchContext.Provider>
  );
}

/**
 * Hook to use job search context.
 */
export const useJobSearch = (): JobSearchContextInterface => {
  return useContext(JobSearchContext);
};
